import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Eye, Dumbbell, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { tr } from "@/data/i18n";

export default function SavedPlans() {
  const { lang, user, loading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    api.listPlans().then(({ data }) => setPlans(data)).catch(() => {}).finally(() => setLoadingPlans(false));
  }, [user, loading, navigate]);

  const del = async (id) => {
    await api.deletePlan(id);
    setPlans((p) => p.filter((x) => x.id !== id));
    toast.success("Eliminata");
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <h1 className="mb-8 font-display text-3xl font-extrabold uppercase text-white sm:text-4xl">{tr(lang, "saved_title")}</h1>
      {loadingPlans ? (
        <p className="text-zinc-500">...</p>
      ) : plans.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-zinc-800 p-12 text-center">
          <Dumbbell className="mx-auto mb-3 text-zinc-600" size={40} />
          <p className="text-zinc-500">{tr(lang, "no_saved")}</p>
          <button onClick={() => navigate("/create/ai")} className="mt-4 inline-flex items-center gap-2 rounded-full bg-red-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-600">
            <Sparkles size={16} /> {tr(lang, "mode_ai_title")}
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {plans.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 transition-all hover:border-red-500/40" data-testid="saved-plan-card">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="truncate font-heading text-lg font-bold text-white">{p.title}</h3>
                  {p.generation > 1 && <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-xs font-bold text-red-400">G{p.generation}</span>}
                </div>
                <div className="mt-1 flex flex-wrap gap-2 text-xs text-zinc-500">
                  <span>{p.goal}</span>·<span>{p.equipment}</span>·<span>{p.days?.length} {tr(lang, "day")}</span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button onClick={() => navigate(`/plan/${p.id}`)} className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-800 hover:text-red-400" data-testid="view-plan-btn" title={tr(lang, "view")}>
                  <Eye size={18} />
                </button>
                <button onClick={() => del(p.id)} className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-800 hover:text-red-500" data-testid="delete-plan-btn" title={tr(lang, "delete")}>
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
